import classNames from "classnames";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useWindowSize } from "hooks/useWindowSize";
import { Button } from "components/DesignSystem/Button/Button";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { RefreshButton } from "components/DesignSystem/RefreshButton/RefreshButton";
import { REFRESH_PAGE } from "constants/refreshPage";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { MagnifyingGlass } from "components/icons/MagnifyingGlass";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { RequestInfo } from "components/Transaction/RequestInfo";
import { AnimatePresence } from "framer-motion";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { ShowControl } from "hooks/useShow";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useShiftRowSelection } from "hooks/useShiftRowSelection";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useTransactionsFilter } from "hooks/useTransactionsFilter";
import useTransactionTableColumn from "hooks/useTransactionTableColumn";
import { parse, stringify } from "qs";
import { ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { flexRender, getCoreRowModel, useReactTable } from "react-table-8.10.7";
import { useGetAllTransactionsDataQuery } from "store/apis/transactions";
import {
  setMessageIdForTransactionList,
  setPopulateTransactionIDs,
} from "store/slices/chat";
import { setFiltersAplied } from "store/slices/transactionCardPreview";
import { resetTxnFilters, setTxnFilters } from "store/slices/transactionFilter";
import {
  openSlider,
  setSelectedTransactionIds,
} from "store/slices/transactions";
import { setTransactionsInChat } from "store/slices/transactionsInChat";
import { closeSimilarTxnsSnackbar } from "store/slices/similarTransactions";
import { RootState } from "store/store";
import type {
  Transaction,
  Transactions as TransactionsType,
  TxnAccountType,
} from "types/Models/books";
import { debounce } from "utils/debouncing";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { EmptyScreen } from "../../pages/Books/EmptyScreen";
import { AddBulkTransaction } from "./AddTransaction/AddBulkTransaction";
import { AutoAssignModal } from "./AutoAssignModal";
import { AutoAssignSnackbar } from "./AutoAssignSnackbar";
import { FixedAssetConfirmationModal } from "./FixedAssetConfirmationModal";
import { PrepaidExpenseModal } from "./PrepaidExpenseModal";
import { TxnFilter } from "./Filter/Filter";
import { RuleConfirmation } from "./RuleConfirmation";
import { TransactionSlider } from "./Slider/TransactionSlider";
import { SplitTransaction } from "./SplitTransaction/SplitTransaction";
import { StripeTxnFilter } from "./StripeTxnFilter";
import { TableSelectionControls } from "./TableSelectionControls";
import {
  TransactionTableHandler,
  transactionColumn,
} from "./TransactionsTable";
import { TransactionRowSelection } from "types/Models/transaction";
import { AnimatedNumbers } from "components/DesignSystem/AnimatedNumbers/AnimatedNumbers";
import { TxnGroupBy } from "./TxnGroupBy";
import { TxnGroupByView } from "./TxnGroupByView";
import {
  TxnGroupBy as TxnGroupByType,
  TXN_GROUP_BY_SORT_COL,
} from "constants/bookkeeping";
import { Divider } from "components/design/Divider";
import { TransactionTableBody } from "./TransactionsTable";

const TRANSACTION_REFRESH_ENDPOINTS = ["getAllTransactionsData"];

export const Transactions = ({
  txnAccountType,
  sideChat,
}: {
  txnAccountType?: TxnAccountType;
  sideChat: ShowControl;
}) => {
  usePageTitle("Transactions");

  const isSmallScreen = useIsSmallScreen();
  const { isMobile } = useWindowSize();

  const refreshButton = (
    <RefreshButton
      endpoints={TRANSACTION_REFRESH_ENDPOINTS}
      page={REFRESH_PAGE.TRANSACTIONS}
    />
  );

  const { TRANSACTION_COLUMNS, updateFilter, values, columnVisibility } =
    useTransactionTableColumn({
      txnAccountType,
    });

  const columnHandler = (
    <TransactionTableHandler
      updateFilter={updateFilter}
      values={values}
      txnAccountType={txnAccountType}
    />
  );

  // On phones the icon columns scroll with the table instead of sticking to the right.
  const rightPinnedColumns = isMobile
    ? []
    : [
        TRANSACTION_COLUMNS.INVOICE,
        TRANSACTION_COLUMNS.MEMO,
        TRANSACTION_COLUMNS.COMMENTS,
        TRANSACTION_COLUMNS.OPTIONS,
      ];

  const [animateTransactions, setAnimateTransactions] = useState<string[]>([]);
  const [sorting, setSorting] = useState<
    {
      desc: boolean;
      id: "AMOUNT" | "DATE" | "VENDOR" | "DESCRIPTION";
    }[]
  >([
    {
      id: TRANSACTION_COLUMNS.DATE,
      desc: true,
    },
  ]);
  const { populateTransactionIDsList, messageIdForTransactionList } =
    useSelector((state: RootState) => state.chat);
  const { update } = useUpdateQuery();
  const dispatch = useAppDispatch();
  const query = useQuery();
  const searchTerm = query.get("search_term");
  const page = query.get("page");
  const pageSize = query.get("page_size");
  const selectedTransactionId = query.get("selected_transaction_id");
  const resetFilter = query.get("reset_filter");
  const transactionId = query.get("transaction_id");
  const groupBy = query.get("group_by") as TxnGroupByType | null;
  const [rowSelection, setRowSelection] = useState({});
  const handleRowSelect = useShiftRowSelection<TransactionsType>();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const location = useLocation();
  const infoItemModal = useModal();
  const [requestInfoTxnId, setRequestInfoTxnId] = useState<string | null>(null);

  const { csvId: openItemCsvId } = useSelector(
    (state: RootState) => state.openItem.addRequestInfoModal
  );

  const { isOpen } = useSelector(
    (state: RootState) => state.similarTransactions
  );
  const { showSplitTransactionModal } = useSelector(
    (state: RootState) => state.splitTransaction
  );
  const allSelectedRows: TransactionRowSelection = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([key, value]) => value)
        .map(([key]) => JSON.parse(key)),
    [rowSelection]
  );

  const selectedTransactionIds = useMemo(
    () => allSelectedRows.map((row) => row.uuid).join(","),
    [allSelectedRows]
  );

  const [duplicatingTransction, setDuplicatingTransaction] = useState<
    Transaction | undefined
  >();
  const [displayCurrency, setDisplayCurrency] = useState("USD");

  useEffect(() => {
    if (transactionId) {
      dispatch(openSlider(transactionId));
    }
  }, [transactionId]);

  const pageNum = Number(page) || 1;

  const sortCol = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc;

  useEffect(() => {
    let currentSearch = parse(location.search, { ignoreQueryPrefix: true });
    if (currentSearch.reset_filter) {
      delete currentSearch.reset_filter;
    }

    if (Boolean(resetFilter)) {
      dispatch(setPopulateTransactionIDs(""));
      dispatch(setMessageIdForTransactionList(""));
      dispatch(resetTxnFilters());
    }

    let timeout: NodeJS.Timeout;
    if (selectedTransactionId || Boolean(resetFilter)) {
      if (currentSearch.selected_transaction_id) {
        delete currentSearch.selected_transaction_id;
      }

      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }

    return () => clearTimeout(timeout);
  }, [resetFilter, selectedTransactionId, page]);

  const effectiveSortCol = groupBy ? TXN_GROUP_BY_SORT_COL[groupBy] : sortCol;
  const effectiveSortOrder = groupBy ? false : sortOrder;

  const allFilters = useTransactionsFilter({
    txnAccountType,
    pageSize,
    searchTerm,
    sortCol: effectiveSortCol,
    sortedToDec: effectiveSortOrder,
    pageNum,
    transactionIds: populateTransactionIDsList,
    messageId: messageIdForTransactionList,
  });

  useEffect(() => {
    dispatch(setFiltersAplied(allFilters));
  }, [allFilters]);

  const {
    data: transactionData,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetAllTransactionsDataQuery(allFilters, {
    skip:
      !groupId || !entityId || infoItemModal.isOpen || Boolean(openItemCsvId),
    refetchOnMountOrArgChange: true,
  });

  const loading = Boolean(populateTransactionIDsList) ? isFetching : isLoading;

  const {
    transactions = [],
    channel_url = "",
    per_page = 1,
    total_count = 1,
    total_pages = 1,
  } = transactionData || {};

  const isEmpty = transactions.length === 0;

  const handleChange = debounce((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search_term", value: value || null });
    dispatch(setTxnFilters({ search_term: value }));
  });

  const onRowClick = (
    transactionId: string,
    e: Event | React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    e.stopPropagation();
    dispatch(openSlider(transactionId));
  };

  const onAnimateComplete = () => {
    let transactionsToSendtoChat = transactions
      .filter((txn) => animateTransactions.includes(txn.transaction.uuid))
      .map((txn) => ({
        id: txn.transaction.uuid,
        amount: txn.transaction.amount,
        source: txn.transaction.source,
        date: txn.transaction.date,
        vendor: {
          logo: txn.transaction.logo,
          name: txn.transaction.merchant,
        },
        isCreditCard: txn.transaction.is_credit_card,
      }));

    transactionsToSendtoChat = [
      ...allSelectedRows.filter(
        (row) =>
          animateTransactions.includes(row.uuid) &&
          !transactionsToSendtoChat.some((txn) => txn.id === row.uuid)
      ),
      ...transactionsToSendtoChat,
    ];

    dispatch(
      setTransactionsInChat({
        transactionsInChat: transactionsToSendtoChat,
        entityId: entityId,
      })
    );
    setAnimateTransactions([]);
    sideChat.show();
  };

  const sendToChat = async ({
    transactionIds,
  }: {
    transactionIds: string[];
  }) => {
    setAnimateTransactions(transactionIds);
    deselectRows();
  };

  const onRequestInfo = ({ transactionId }: { transactionId?: string }) => {
    infoItemModal.open();
    if (transactionId) {
      setRequestInfoTxnId(transactionId);
    }
  };

  const transactionsTableData = useMemo(
    () => transactionData?.transactions || [],
    [JSON.stringify(transactionData?.transactions)]
  );

  const onDuplicate = (transaction: Transaction) => {
    addTransactionModal.open();
    setDuplicatingTransaction(transaction);
  };

  const closeAddTransaction = () => {
    addTransactionModal.close();
    setDuplicatingTransaction(undefined);
  };

  const isStripeAccountView = txnAccountType === "STRIPE";
  const hasRevenueWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );
  const hasTransactionWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );

  const columns = useMemo(
    () => [
      transactionColumn.select(selectedTransactionIds, handleRowSelect),
      transactionColumn.date(),
      transactionColumn.from(),
      transactionColumn.merchant(),
      transactionColumn.description(),
      transactionColumn.amount(),
      transactionColumn.category({ isDisabled: isStripeAccountView }),
      transactionColumn.classification(),
      transactionColumn.invoice({
        hasWritePermission: hasRevenueWritePermission,
      }),
      transactionColumn.suggested({
        hasWritePermission: hasRevenueWritePermission,
      }),
      ...(isStripeAccountView ? [transactionColumn.daily_entry()] : []),
      transactionColumn.memo({
        hasWritePermission: hasTransactionWritePermission,
      }),
      transactionColumn.comments({
        hasWritePermission: hasTransactionWritePermission,
      }),

      transactionColumn.options({
        onDuplicate,
        onRequestInfo,
        sendToChat,
        onRowClick,
      }),
    ],
    [
      selectedTransactionIds,
      handleRowSelect,
      hasRevenueWritePermission,
      hasTransactionWritePermission,
      isStripeAccountView,
    ]
  );

  const table = useReactTable({
    data: transactionsTableData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    manualSorting: true,
    state: {
      columnVisibility: columnVisibility,
      rowSelection,
      sorting,
      columnPinning: {
        right: rightPinnedColumns,
      },
    },
    // @ts-ignore
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    meta: { displayCurrency, setDisplayCurrency },
    enableMultiSort: false,
    enableSortingRemoval: false,
    getRowId: (row) =>
      JSON.stringify({
        uuid: row.transaction.uuid,
        category_id: row.transaction.category?.uuid,
        id: row.transaction.uuid,
        amount: row.transaction.amount,
        source: row.transaction.source,
        date: row.transaction.date,
        vendor: {
          logo: row.transaction.logo,
          name: row.transaction.merchant,
        },
        isCreditCard: row.transaction.is_credit_card,
      }),
  });

  const deselectRows = () => {
    table.resetRowSelection();
    dispatch(setSelectedTransactionIds([]));
    dispatch(closeSimilarTxnsSnackbar());
    setRequestInfoTxnId(null);
  };

  const addTransactionModal = useModal();
  const openConfirmation = useModal();

  const isRowsSelected = allSelectedRows.length > 0;

  const unSelectedRowsIds = transactionsTableData
    .filter(
      (txn) =>
        !table
          .getSelectedRowModel()
          .flatRows.find(
            (selectedTxn) =>
              selectedTxn.original.transaction.uuid === txn.transaction.uuid
          )
    )
    .map(({ transaction }) => transaction.uuid);

  const resetViewTransactions = () => {
    dispatch(setPopulateTransactionIDs(""));
    dispatch(setMessageIdForTransactionList(""));
  };

  const firstPinnedColumn = table
    .getAllColumns()
    .filter((c) => c.id !== TRANSACTION_COLUMNS.OPTIONS)
    .find((c) => c.getIsVisible() && c.getIsPinned());

  const subtotal = useMemo(
    () => allSelectedRows.reduce((sum, row) => sum + (row.amount || 0), 0),
    [allSelectedRows]
  );
  const hasSubtotal = subtotal !== 0;

  const actionIconColumn = [
    TRANSACTION_COLUMNS.SELECT as string,
    TRANSACTION_COLUMNS.OPTIONS as string,
    TRANSACTION_COLUMNS.MEMO as string,
    TRANSACTION_COLUMNS.COMMENTS as string,
    TRANSACTION_COLUMNS.INVOICE as string,
  ];

  return (
    <>
      <DashboardContainer className="t-gap-4 t-pl-5 t-h-full">
        <DashboardContainer.Header sticky>
          <div className="t-bg-surface t-flex t-flex-col t-gap-4 t-pt-5">
            <div className="t-flex t-gap-4 sm:t-flex-col sm:t-gap-2">
              <div className="t-flex t-gap-2 t-w-full t-items-center t-flex-wrap sm:t-flex-nowrap">
                <Search
                  className="t-w-[18%] focus:t-w-3/6 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
                  customSize="xsmall"
                  onChange={handleChange}
                  defaultValue={searchTerm || ""}
                  placeholder="Search"
                  isLoading={isFetching}
                />
                <AnimatePresence>
                  {populateTransactionIDsList && (
                    <Button
                      customType="secondary"
                      onClick={resetViewTransactions}
                      size="small"
                    >
                      <div className="t-flex t-text-body-sm t-text-text-30 t-items-center t-gap-1">
                        <Cross />
                        {total_count} selected
                      </div>
                    </Button>
                  )}
                  <TxnGroupBy groupBy={groupBy} />
                  <Divider orientation="vertical" classNames="t-h-6" />
                  {isStripeAccountView ? <StripeTxnFilter /> : <TxnFilter />}
                </AnimatePresence>
                {isMobile && refreshButton}
              </div>

              <div className="t-grow t-ml-auto t-shrink-0 t-flex t-items-center t-gap-2 sm:t-ml-0 sm:t-justify-end">
                {isSuccess && (
                  <PaginationHandler
                    page_num={pageNum}
                    per_page={per_page}
                    total_count={total_count}
                    total_pages={total_pages}
                    showOptions={!isSmallScreen}
                    size="xsmall"
                    isLoading={isFetching}
                  />
                )}
                {!isMobile && refreshButton}
              </div>
            </div>
          </div>
        </DashboardContainer.Header>
        <DashboardContainer.Content className="!t-overflow-hidden">
          <Async.Root {...{ isLoading: loading, isEmpty, isSuccess }}>
            <Async.Empty>
              <EmptyScreen text="No transactions found">
                <span className="t-text-i-neutral-10">
                  <MagnifyingGlass size="149" />
                </span>
              </EmptyScreen>
            </Async.Empty>
            <Async.Success>
              <div className="t-relative">
                {groupBy ? (
                  <TxnGroupByView
                    groupBy={groupBy}
                    transactions={transactionsTableData}
                    table={table}
                    onRowClick={onRowClick}
                    isRowsSelected={isRowsSelected}
                    actionIconColumn={actionIconColumn}
                    firstPinnedColumn={firstPinnedColumn?.id}
                    animateTransactions={animateTransactions}
                    onAnimateComplete={onAnimateComplete}
                    updateFilter={updateFilter}
                    values={values}
                    txnAccountType={txnAccountType}
                  />
                ) : (
                  <>
                    {!isMobile && (
                      <div className="t-absolute t-top-0 t-right-0 t-z-filter-head t-pt-1 t-mr-1">
                        {columnHandler}
                      </div>
                    )}
                    <Table.Container
                      className="t-overflow-auto scrollbar-visible t-max-h-[calc(100vh-230px)] t-w-full t-pb-32 t-rounded-tl-lg t-rounded-tr-lg"
                      sticky
                    >
                      <Table.Content>
                        <Table.Head>
                          {table.getHeaderGroups().map((headerGroup) => {
                            return (
                              <Table.HeadRow key={headerGroup.id}>
                                {headerGroup.headers.map((header, index) => {
                                  const styles = getCommonPinningStyles(
                                    header.column
                                  );

                                  const isPinnedColumn =
                                    header.column.getIsPinned();
                                  const isLastHeader =
                                    index === headerGroup.headers.length - 1;

                                  return (
                                    <Table.HeadCell
                                      className={classNames(
                                        "t-text-subtext-sm t-group",
                                        {
                                          "!t-bg-surface-lighter-grey":
                                            isPinnedColumn,
                                          "t-drop-shadow-table-left-pin":
                                            firstPinnedColumn?.id === header.id,
                                        }
                                      )}
                                      size={
                                        actionIconColumn.includes(header.id)
                                          ? "small"
                                          : "regular"
                                      }
                                      key={header.id}
                                      style={{
                                        minWidth: header.getSize(),
                                        ...styles,
                                      }}
                                      isUpperCase={false}
                                    >
                                      <span className="t-flex t-gap-1 t-items-center">
                                        {header.isPlaceholder
                                          ? null
                                          : flexRender(
                                              header.column.columnDef.header,
                                              header.getContext()
                                            )}
                                        {isMobile &&
                                          isLastHeader &&
                                          columnHandler}
                                      </span>
                                    </Table.HeadCell>
                                  );
                                })}
                              </Table.HeadRow>
                            );
                          })}
                        </Table.Head>
                        <TransactionTableBody
                          rows={table.getRowModel().rows}
                          onRowClick={onRowClick}
                          isRowsSelected={isRowsSelected}
                          actionIconColumn={actionIconColumn}
                          firstPinnedColumn={firstPinnedColumn?.id}
                          animateTransactions={animateTransactions}
                          onAnimateComplete={onAnimateComplete}
                        />
                      </Table.Content>
                    </Table.Container>
                  </>
                )}
              </div>

              {!allSelectedRows.length && <AutoAssignSnackbar />}
              {isOpen && (
                <AutoAssignModal openConfirmation={openConfirmation.open} />
              )}
              <PrepaidExpenseModal />
              <FixedAssetConfirmationModal />
              <RuleConfirmation
                isOpen={openConfirmation.isOpen}
                close={openConfirmation.close}
              />
            </Async.Success>
          </Async.Root>
          <SelectedFloatingControl>
            {allSelectedRows.length > 0 && (
              <>
                <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
                  {allSelectedRows.length} selected
                </span>
                <span className="t-text-neutral-10 t-h-full t-flex">
                  <DividerLine />
                </span>
                {hasSubtotal && (
                  <>
                    <div className="t-flex t-items-center t-gap-1 t-text-text-60 t-text-subtext sm:t-hidden">
                      <span>Subtotal:</span>

                      <AnimatedNumbers amount={subtotal} />
                    </div>
                    <span className="t-text-neutral-10 t-h-full t-flex sm:t-hidden">
                      <DividerLine />
                    </span>
                  </>
                )}
                <TableSelectionControls
                  unSelectedRowsIds={unSelectedRowsIds}
                  txnAccountType={txnAccountType}
                  isAllTxnSelected={false}
                  table={table}
                  sendToChat={sendToChat}
                  onRequestInfo={onRequestInfo}
                  deselectRows={deselectRows}
                  selectionCount={allSelectedRows.length}
                  allSelectedRows={allSelectedRows}
                />
                <Button
                  customType="ghost_icon"
                  size="xsmall"
                  onClick={() => {
                    table.resetRowSelection();
                    dispatch(closeSimilarTxnsSnackbar());
                  }}
                >
                  <Cross />
                </Button>
              </>
            )}
          </SelectedFloatingControl>
        </DashboardContainer.Content>
      </DashboardContainer>

      {infoItemModal.isOpen && (
        <RequestInfo
          isOpen={infoItemModal.isOpen}
          onClose={infoItemModal.close}
          channelUrl={channel_url}
          transactionIds={
            allSelectedRows.map(({ uuid }) => uuid).join(",") ||
            requestInfoTxnId ||
            ""
          }
          deselectRows={deselectRows}
          setRowSelection={setRowSelection}
        />
      )}

      <TransactionSlider
        sendToChat={({ transactionId }) =>
          sendToChat({
            transactionIds: [transactionId],
          })
        }
        onDuplicate={onDuplicate}
      />
      <AddBulkTransaction
        defaultTransaction={duplicatingTransction}
        close={closeAddTransaction}
        isOpen={addTransactionModal.isOpen}
        openAddTransactionModal={addTransactionModal.open}
      />
      {showSplitTransactionModal && (
        <SplitTransaction isOpen={showSplitTransactionModal} />
      )}
    </>
  );
};
